import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ExternalLink } from "lucide-react";
import { getBlockMediaInfo } from "@cliently/schemas";
import { cn, ImageZoomDialog } from "@cliently/ui";
import { useState } from "react";

type ProgramBlockMediaProps = {
  url: string | null | undefined;
  className?: string;
  resolveImageUrl?: (url: string) => string | undefined;
};

export const ProgramBlockMedia = ({
  url,
  className,
  resolveImageUrl,
}: ProgramBlockMediaProps) => {
  const { t } = useTranslation();
  const [zoomOpen, setZoomOpen] = useState(false);
  const media = useMemo(() => getBlockMediaInfo(url), [url]);

  if (!media) return null;

  if (media.kind === "image") {
    const src =
      media.src.startsWith("data:image/")
        ? media.src
        : (resolveImageUrl?.(media.src) ?? media.src);

    return (
      <>
        <button
          type="button"
          onClick={() => setZoomOpen(true)}
          className={cn(
            "block w-full overflow-hidden rounded-xl border bg-muted focus-visible:ring-primary/40 focus-visible:outline-none focus-visible:ring-2",
            className,
          )}
        >
          <img
            src={src}
            alt={t("programs.blockImage", "Illustration du bloc")}
            className="aspect-video max-h-72 w-full object-contain"
          />
        </button>
        <ImageZoomDialog
          src={src}
          alt={t("programs.blockImage", "Illustration du bloc")}
          open={zoomOpen}
          onOpenChange={setZoomOpen}
        />
      </>
    );
  }

  if (media.kind === "youtube" || media.kind === "vimeo") {
    return (
      <div
        className={cn(
          "aspect-video w-full overflow-hidden rounded-xl border bg-muted",
          className,
        )}
      >
        <iframe
          title={t("programs.blockVideo", "Vidéo du bloc")}
          src={media.embedUrl}
          className="h-full min-h-[180px] w-full border-0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          loading="lazy"
        />
      </div>
    );
  }

  return (
    <a
      href={media.href}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        "text-primary inline-flex items-center gap-1.5 text-sm font-medium hover:underline",
        className,
      )}
    >
      <ExternalLink className="h-3.5 w-3.5" aria-hidden />
      {t("programs.openBlockVideo", "Voir la vidéo")}
    </a>
  );
};
